import { Routes, Route, Outlet } from 'react-router';
import { Flex } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { withTranslation } from '@application/i18n/with_translation';
import { useTitle } from '@application/use_title';
import { useResolveController } from '@visiba/instantiation';
import { AnalyticCategories } from '@application/analytic/analytic.service';
import { AnalyticProvider, useAnalyticContext } from '@application/analytic/analytic_context.provider';
import { Shimmer } from '@component/new_shimmer.component';
import { Async } from '@component/async.component';
import { TopBar } from '@component/top_bar.component';
import { ScreenClosed } from './screen_closed/screen_closed.component';
import { ScreenError } from './screen_error/screen_error.component';
import { ScreenRedRobinSessionIframe } from './screen_session_iframe/screen_session_iframe.component';
import { ScreenWhoIsRespondant } from './screen_who_is_respondant/screen_who_is_respondant.component';
import { ViewRedRobinController } from './view_red_robin.controller';
import styled from 'styled-components';

export function ViewRedRobinSkeleton(): JSX.Element {
	return (
		<StyledContainer>
			<Flex gap={tokens.spacing.x3} grow='1'>
				<Shimmer height={40} width='50%' />

				<Flex gap={tokens.spacing.x1}>
					<Shimmer height={250} width='80%' />
					<Shimmer height={30} width='30%' />
				</Flex>
			</Flex>
		</StyledContainer>
	);
}
export function ViewRedRobin(): JSX.Element {
	const controller = useResolveController(ViewRedRobinController);
	const analytics = useAnalyticContext();

	useTitle(withTranslation('View.RedRobin.Title'));

	function handleAbort() {
		analytics.trackEvent('Clicked abort button');

		controller.goToReceptionHome();
	}

	return (
		<AnalyticProvider category={AnalyticCategories.RedRobin}>
			<Flex grow='1'>
				<TopBar.Content title={withTranslation('View.RedRobin.Title')} onAbort={handleAbort} />
				<Async derive={controller.flowData} fallback={<ViewRedRobinSkeleton />} catch={<ScreenError />}>
					{(flowData) => {
						if (!flowData) {
							return null;
						}

						return (
							<Routes>
								<Route element={<PaddingOutlet />}>
									<Route path='' element={<ScreenWhoIsRespondant />} />
									<Route path='/closed' element={<ScreenClosed />} />
									<Route path='/error/*' element={<ScreenError />} />
								</Route>
								<Route path=':sessionId' element={<ScreenRedRobinSessionIframe />} />
							</Routes>
						);
					}}
				</Async>
			</Flex>
		</AnalyticProvider>
	);
}

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	padding: ${tokens.spacing.x2};

	${tokens.breakpointQuery.small} {
		padding: ${tokens.spacing.x3} ${tokens.spacing.x4} ${tokens.spacing.x4};
		max-width: 580px;
		width: 100%;
		margin: 0 auto;
	}
`;
// Ideally we want to remove these.
function PaddingOutlet() {
	return (
		<StyledContainer>
			<Outlet />
		</StyledContainer>
	);
}
